import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, PaperPlaneRight, Microphone, Paperclip, Sparkle } from "@phosphor-icons/react";
import { api, ensureUserId } from "@/lib/api";

export default function Chat() {
  const { id } = useParams();
  const nav = useNavigate();
  const [mentor, setMentor] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const sessionId = useRef(ensureUserId());
  const scrollRef = useRef(null);

  useEffect(() => {
    api.get(`/mentors/${id}`).then((r) => {
      setMentor(r.data);
      const greet = `Hi — I'm ${r.data.name}. I coach in ${r.data.domain.toLowerCase()}. What's on your mind today?`;
      setMessages([{ role: "assistant", text: greet }]);
    });
  }, [id]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  const send = async (text) => {
    const t = (text ?? input).trim();
    if (!t || loading) return;
    setMessages((m) => [...m, { role: "user", text: t }]);
    setInput("");
    setLoading(true);
    try {
      const r = await api.post("/chat", { session_id: sessionId.current, mentor_id: id, message: t });
      setMessages((m) => [...m, { role: "assistant", text: r.data.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", text: "Connection hiccup. Try again in a moment." }]);
    } finally {
      setLoading(false);
    }
  };

  const SUGGESTIONS = mentor ? [
    "Where do I even start?",
    "Stress-test my idea in 30 seconds",
    "Give me one small action for today",
  ] : [];

  return (
    <div className="min-h-screen bg-[#050505] flex flex-col" data-testid="chat-screen">
      <header className="sticky top-0 z-30 px-4 pt-12 pb-3 glass-strong">
        <div className="flex items-center gap-3">
          <button onClick={() => nav(-1)} data-testid="chat-back" className="w-9 h-9 rounded-full bg-white/5 flex items-center justify-center active:scale-95">
            <ArrowLeft size={16} />
          </button>
          {mentor && (
            <>
              <img src={mentor.avatar} alt={mentor.name} className="w-9 h-9 rounded-full mentor-thumb" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{mentor.name}</div>
                <div className="text-[10px] uppercase tracking-widest text-emerald-400 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 inline-block animate-pulse" /> Online
                </div>
              </div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1">
                <Sparkle size={10} weight="fill" /> AI Mentor
              </div>
            </>
          )}
        </div>
      </header>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-6 space-y-4">
        <AnimatePresence initial={false}>
          {messages.map((m, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
            >
              <div
                data-testid={`msg-${m.role}-${i}`}
                className={`max-w-[82%] px-4 py-3 rounded-3xl text-[14px] leading-relaxed whitespace-pre-wrap ${
                  m.role === "user"
                    ? "bg-white text-black rounded-br-md"
                    : "bg-white/5 text-zinc-100 rounded-bl-md border border-white/5"
                }`}
              >
                {m.text}
              </div>
            </motion.div>
          ))}
        </AnimatePresence>

        {loading && (
          <div className="flex justify-start">
            <div className="bg-white/5 px-4 py-3 rounded-3xl rounded-bl-md flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-white/50 animate-bounce" style={{ animationDelay: "0ms" }} />
              <span className="w-1.5 h-1.5 rounded-full bg-white/50 animate-bounce" style={{ animationDelay: "120ms" }} />
              <span className="w-1.5 h-1.5 rounded-full bg-white/50 animate-bounce" style={{ animationDelay: "240ms" }} />
            </div>
          </div>
        )}
      </div>

      {messages.length <= 1 && !loading && (
        <div className="px-4 pb-2 flex flex-wrap gap-2">
          {SUGGESTIONS.map((s) => (
            <button
              key={s}
              data-testid={`suggest-${s}`}
              onClick={() => send(s)}
              className="text-[12px] glass rounded-full px-3 py-1.5 text-zinc-200 active:scale-95 transition"
            >
              {s}
            </button>
          ))}
        </div>
      )}

      <div className="sticky bottom-0 px-4 pt-2 pb-5 bg-gradient-to-t from-[#050505] via-[#050505] to-transparent">
        <div className="glass-strong rounded-full pl-4 pr-1.5 py-1.5 flex items-center gap-2">
          <button data-testid="attach-btn" className="text-zinc-400 active:scale-95"><Paperclip size={18} /></button>
          <input
            data-testid="chat-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder="Ask anything…"
            className="bg-transparent flex-1 outline-none text-sm py-2 placeholder:text-zinc-500"
          />
          {input ? (
            <button
              data-testid="send-btn"
              onClick={() => send()}
              className="w-10 h-10 rounded-full bg-white text-black flex items-center justify-center active:scale-95"
            >
              <PaperPlaneRight size={16} weight="fill" />
            </button>
          ) : (
            <button data-testid="voice-input-btn" className="w-10 h-10 rounded-full bg-white/10 text-white flex items-center justify-center active:scale-95">
              <Microphone size={16} weight="fill" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
